import { useEffect, useState, useRef } from "react";
import ProductFeedback from './ProductFeedback';
import { API, authHeaders } from '../services/apiConfig';
import './ProductDetails.css';

const resolveImg = (url) => {
  if (!url) return 'images/product.jpg';
  if (url.startsWith('http')) return url;
  return `${API}${url}`;
};

function RelatedSlider({ related, onProductClick, resolveImg }) {
  const [idx, setIdx] = useState(0);
  const visible = 4;
  const canPrev = idx > 0;
  const canNext = idx + visible < related.length;

  const arrowBtn = (disabled, onClick, label) => (
    <button onClick={onClick} disabled={disabled}
      style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1.5px solid #e0e0e0', background: disabled ? '#f5f5f5' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? '#ccc' : '#333', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}
      onMouseOver={e => { if (!disabled) e.currentTarget.style.background = '#ffe8e8'; }}
      onMouseOut={e => { if (!disabled) e.currentTarget.style.background = '#fff'; }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: '28px 32px', background: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#1a1a1a', margin: 0, flex: 1 }}>✨ You Might Also Like</h3>
        {arrowBtn(!canPrev, () => setIdx(i => i - 1), '‹')}
        {arrowBtn(!canNext, () => setIdx(i => i + 1), '›')}
      </div>
      <div className="pd-related-grid">
        {related.slice(idx, idx + visible).map(p => (
          <div key={p.id} onClick={() => onProductClick && onProductClick(p.id)}
            style={{ background: 'white', borderRadius: '14px', padding: '12px', cursor: 'pointer', border: '1px solid #f0f0f0', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,0,0,0.12)'; e.currentTarget.style.borderColor = 'rgba(255,0,0,0.2)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; e.currentTarget.style.borderColor = '#f0f0f0'; }}>
            <div style={{ background: '#f9f9f9', borderRadius: '10px', padding: '8px', marginBottom: '8px' }}>
              <img src={resolveImg(p.mainImage)} style={{ height: '100px', width: '100%', objectFit: 'contain' }} alt={p.name} />
            </div>
            <p style={{ fontSize: '12px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 4px 0', lineHeight: '1.4' }}>{p.name}</p>
            <p style={{ fontSize: '15px', fontWeight: '800', color: '#ff0000', margin: 0 }}>₹{p.price}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Toast({ message, type, onDone, onGoToCart }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, []);

  const isSuccess = type === 'success';
  return (
    <div style={{
      position: 'fixed', bottom: '32px', right: '32px', zIndex: 99999,
      background: isSuccess ? '#9A1111' : '#107FDF',
      color: 'white', padding: '28px 24px', borderRadius: '25px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px',
      minWidth: '280px', maxWidth: '320px', textAlign: 'center',
      animation: 'toastIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px' }}>
        {isSuccess ? '🛒' : 'ℹ'}
      </div>
      <span style={{ fontWeight: '600', fontSize: '15px', lineHeight: '1.5' }}>{message}</span>
      {isSuccess && (
        <button onClick={onGoToCart} style={{ background: 'white', color: '#9A1111', border: 'none', borderRadius: '25px', padding: '10px 28px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          onMouseOver={e => e.currentTarget.style.transform = 'scale(1.04)'}
          onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
          Go to Cart
        </button>
      )}
      <style>{`@keyframes toastIn { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

export default function ProductDetails({ productId, onClose, onProductClick, onGoToCart }) {
  const [product, setProduct]                 = useState(null);
  const [variants, setVariants]               = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentImage, setCurrentImage]       = useState(null);
  const [allImages, setAllImages]             = useState([]);
  const [qty, setQty]                         = useState(1);
  const [related, setRelated]                 = useState([]);
  const [toast, setToast]                     = useState(null);
  const mainImgRef = useRef(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const handleAddToCart = async () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user) { showToast('Please login to add to cart', 'error'); return; }
    try {
      await fetch(`${API}/api/cart`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userId: user.userId, productId: product.id, variantId: selectedVariant?.id || null, quantity: qty })
      });
      showToast(`Item added to cart!`, 'success');
    } catch {
      showToast('Failed to add to cart', 'error');
    }
  };

  useEffect(() => {
    if (!productId) return;
    fetch(`${API}/api/products/${productId}`)
      .then(r => r.json()).then(data => {
        setProduct(data);
        setCurrentImage(data.mainImage);
        setAllImages([data.mainImage, ...(data.additionalImages || [])].filter(Boolean));
      });
    fetch(`${API}/api/products/${productId}/variants`)
      .then(r => r.json()).then(data => {
        setVariants(data);
        const def = data.find(v => v.isDefault) || data[0];
        if (def) { setSelectedVariant(def); if (def.images?.[0]) setCurrentImage(def.images[0]); }
      });
    fetch(`${API}/api/products`)
      .then(r => r.json()).then(data => setRelated(data.filter(p => p.id !== productId).slice(0, 12)));
  }, [productId]);

  if (!productId) return null;
  if (!product) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: 'white', padding: '40px', borderRadius: '20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Loading...</div>
    </div>
  );

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const cat = product?.category?.toUpperCase();
  const catColor = cat === 'SUPER_PREMIUM' || cat === 'SUPER-PREMIUM' ? '#9A1111' : cat === 'PREMIUM' ? '#0A683C' : '#107FDF';

  return (
    <>
      <div className="pd-overlay-fix" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 9999, overflowY: 'auto', backdropFilter: 'blur(1.8px)', padding: window.innerWidth <= 768 ? '70px 16px 24px' : '24px 16px' }} onClick={onClose}>
        <div style={{ background: '#fff', maxWidth: '1100px', width: '100%', borderRadius: '24px', boxShadow: '0 30px 80px rgba(0,0,0,0.4)', overflow: 'hidden', position: 'relative' }} onClick={e => e.stopPropagation()}>

          {/* Close */}
          <button onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10, width: '36px', height: '36px', borderRadius: '50%', background: 'transparent', color: '#9A1111', border: 'none', cursor: 'pointer', fontSize: '24px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
            onMouseOver={e => { e.currentTarget.style.transform = 'rotate(90deg) scale(1.1)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'rotate(0) scale(1)'; }}>×</button>

          {/* Top section */}
          <div className="pd-top-section">

            {/* ── Image panel ── */}
            <div className="pd-image-panel">

              {/* Main image */}
              <div style={{
                background: 'white', borderRadius: '16px',
                border: '1px solid #ececec', overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '560px', width: '100%',
              }}>
                <img
                  ref={mainImgRef}
                  src={resolveImg(currentImage || product.mainImage)}
                  alt={product.name}
                  style={{ height: '520px', width: '100%', objectFit: 'contain', display: 'block' }}
                />
              </div>

              {/* Thumbnail strip — horizontal, centered below main image */}
              {allImages.length > 1 && (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', overflowX: 'auto', paddingBottom: '2px', justifyContent: 'center' }}>
                  {allImages.map((img, idx) => (
                    <div key={idx}
                      onClick={() => setCurrentImage(img)}
                      style={{
                        width: '64px', height: '64px', flexShrink: 0, borderRadius: '10px',
                        overflow: 'hidden', cursor: 'pointer',
                        border: currentImage === img ? '2.5px solid #ff0000' : '2px solid #e0e0e0',
                        boxShadow: currentImage === img ? '0 0 0 3px rgba(255,0,0,0.1)' : '0 1px 4px rgba(0,0,0,0.07)',
                        transform: currentImage === img ? 'scale(1.06)' : 'scale(1)',
                        transition: 'all 0.2s', background: '#fff',
                      }}
                      onMouseOver={e => { if (currentImage !== img) e.currentTarget.style.borderColor = '#ffaaaa'; }}
                      onMouseOut={e => { if (currentImage !== img) e.currentTarget.style.borderColor = '#e0e0e0'; }}
                    >
                      <img src={resolveImg(img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Info panel ── */}
            <div className="pd-info-panel">

              <span style={{ display: 'inline-block', background: catColor, color: 'white', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px', alignSelf: 'flex-start' }}>
                {product.category?.replace('_', ' ')}
              </span>

              <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1a1a1a', lineHeight: '1.3', margin: '0 0 12px 0' }}>{product.name}</h1>

              {product.description && (
                <p style={{ fontSize: '14px', color: '#555', lineHeight: '1.7', margin: '0 0 16px 0' }}>{product.description}</p>
              )}

              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '14px' }}>
                <span style={{ fontSize: '32px', fontWeight: '800', color: catColor }}>₹{product.price}</span>
                <span style={{ fontSize: '13px', color: '#888', fontWeight: '500' }}>per piece </span>
              </div>


              {variants.length > 0 && variants.some(v => v.color) && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#888', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>Select Color</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {variants.filter(v => v.color).map(v => {
                      const colorName = v.color.toLowerCase().trim();
                      // Map common color names to CSS colors
                      const colorMap = {
                        black: '#111111', white: '#ffffff', red: '#cc0000', blue: '#1565c0',
                        green: '#2e7d32', yellow: '#f9a825', orange: '#e65100', pink: '#c2185b',
                        purple: '#6a1b9a', brown: '#5d4037', grey: '#616161', gray: '#616161',
                        navy: '#1a237e', maroon: '#880e4f', gold: '#f9a825', silver: '#9e9e9e',
                        beige: '#d7ccc8', cream: '#fff8e1', teal: '#00695c', cyan: '#00838f',
                        indigo: '#283593', violet: '#4527a0', magenta: '#ad1457', lime: '#558b2f',
                      };
                      const bgColor = colorMap[colorName] || colorName;
                      // Determine if background is dark to pick text color
                      const darkColors = ['black','#111111','navy','#1a237e','maroon','#880e4f','brown','#5d4037','purple','#6a1b9a','indigo','#283593','violet','#4527a0','red','#cc0000','green','#2e7d32','blue','#1565c0','teal','#00695c','cyan','#00838f','magenta','#ad1457','grey','#616161','gray','#616161'];
                      const isDark = darkColors.includes(colorName) || darkColors.includes(bgColor);
                      const textColor = isDark ? '#ffffff' : '#111111';
                      const isSelected = selectedVariant?.id === v.id;
                      return (
                        <button key={v.id} onClick={() => { setSelectedVariant(v); setCurrentImage(v.images?.[0] || product.mainImage); }}
                          style={{
                            padding: '7px 18px', borderRadius: '25px',
                            border: isSelected ? `2px solid ${bgColor}` : `1.5px solid #ccc`,
                            background: isSelected ? bgColor : 'white',
                            color: isSelected ? textColor : '#333',
                            fontWeight: '600', fontSize: '13px', cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: isSelected ? `0 4px 12px ${bgColor}66` : 'none',
                            outline: isSelected ? `2px solid ${bgColor}` : 'none',
                            outlineOffset: '2px',
                          }}
                          onMouseOver={e => { e.currentTarget.style.background = bgColor; e.currentTarget.style.color = textColor; e.currentTarget.style.border = `2px solid ${bgColor}`; e.currentTarget.style.boxShadow = `0 4px 12px ${bgColor}66`; }}
                          onMouseOut={e => { e.currentTarget.style.background = isSelected ? bgColor : 'white'; e.currentTarget.style.color = isSelected ? textColor : '#333'; e.currentTarget.style.border = isSelected ? `2px solid ${bgColor}` : '1.5px solid #ccc'; e.currentTarget.style.boxShadow = isSelected ? `0 4px 12px ${bgColor}66` : 'none'; }}>
                          {v.color}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#888', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>Quantity</div>
                  <div style={{ display: 'flex', alignItems: 'center', border: '2px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
                    <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: '38px', height: '42px', border: 'none', background: '#f5f5f5', cursor: 'pointer', fontSize: '18px', fontWeight: '700', color: '#333', transition: 'background 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.background = '#ffe8e8'}
                      onMouseOut={e => e.currentTarget.style.background = '#f5f5f5'}>−</button>
                    <span style={{ width: '44px', textAlign: 'center', fontSize: '15px', fontWeight: '700', color: '#1a1a1a' }}>{qty}</span>
                    <button onClick={() => setQty(q => q + 1)} style={{ width: '38px', height: '42px', border: 'none', background: '#f5f5f5', cursor: 'pointer', fontSize: '18px', fontWeight: '700', color: '#333', transition: 'background 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.background = '#ffe8e8'}
                      onMouseOut={e => e.currentTarget.style.background = '#f5f5f5'}>+</button>
                  </div>
                </div>
                <button onClick={handleAddToCart}
                  style={{ flex: 1, height: '44px', background: '#9A1111', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 16px rgba(154,17,17,0.35)', transition: 'all 0.2s', letterSpacing: '0.5px' }}
                  onMouseOver={e => { e.currentTarget.style.background = '#7F1D1D'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(127,29,29,0.45)'; }}
                  onMouseOut={e => { e.currentTarget.style.background = '#9A1111'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(154,17,17,0.35)'; }}>
                  🛒 ADD TO CART
                </button>
              </div>

              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <div style={{ background: '#fafafa', borderRadius: '12px', padding: '14px', border: '1px solid #f0f0f0', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#888', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>Specifications</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {Object.entries(product.specifications).map(([k, v]) => (
                      <div key={k} style={{ background: 'white', padding: '8px 12px', borderRadius: '8px', border: '1px solid #eee' }}>
                        <div style={{ fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k}</div>
                        <div style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: '600', marginTop: '2px' }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.details && (
                <details style={{ border: '1px solid #f0f0f0', borderRadius: '12px', padding: '12px 16px', background: 'white' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: '700', fontSize: '14px', color: '#333' }}>📝 Product Details</summary>
                  <p style={{ marginTop: '10px', color: '#555', lineHeight: '1.7', fontSize: '13px' }}>{product.details}</p>
                </details>
              )}
            </div>
          </div>

          {/* You might also like */}
          {related.length > 0 && (
            <RelatedSlider related={related} onProductClick={onProductClick} resolveImg={resolveImg} />
          )}

          {/* Reviews */}
          <div style={{ padding: '28px 32px', borderTop: '1px solid #f0f0f0' }}>
            <ProductFeedback
              productId={productId}
              userId={user?.userId}
              userName={user?.name && user.name !== 'null' && user.name.trim() ? user.name : ''}
              userEmail={user?.email || ''}
            />
          </div>


        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} onGoToCart={onGoToCart} />}
    </>
  );
}
